import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { DarkModeProvider } from './app/contexts/DarkModeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { VersionGate } from './app/components/VersionGate';
import { router } from './router';
import { initSentry } from './lib/sentry';
import './styles/index.css';

initSentry();

// Note: window.onerror and unhandledrejection are handled automatically
// by @sentry/react v10's globalHandlersIntegration (included by default).

// Native app integrations (only active on Android/iOS, no-op on web)
if (Capacitor.isNativePlatform()) {

  // Android back button: hierarchy-based navigation instead of history.back()
  // This prevents unpredictable back behavior (e.g., returning to payment pages,
  // navigating through tab history linearly, or blank pages on deep links).
  App.addListener('backButton', () => {
    const pathname = window.location.pathname;
    const search = window.location.search;

    // 1. If query params are open (overlays like filter, QR, recharge), strip them
    const overlayParams = ['filter', 'qr', 'recharge', 'transaction', 'order_id', 'amount'];
    if (search) {
      const params = new URLSearchParams(search);
      const hasOverlay = overlayParams.some((p) => params.has(p));
      if (hasOverlay) {
        router.navigate(pathname, { replace: true });
        return;
      }
    }

    // 2. Child routes → navigate to parent
    // /app/location/:id/chargepoint/:cpId → /app/location/:id
    const cpMatch = pathname.match(/^\/app\/location\/([^/]+)\/chargepoint\/.+$/);
    if (cpMatch) {
      router.navigate(`/app/location/${cpMatch[1]}`, { replace: true });
      return;
    }
    // /app/location/:id → /app/map
    if (/^\/app\/location\/[^/]+$/.test(pathname)) {
      router.navigate('/app/map', { replace: true });
      return;
    }
    // /app/activity/charging/:id or /app/activity/summary/:id → /app/activity
    if (/^\/app\/activity\/(charging|summary)\/.+$/.test(pathname)) {
      router.navigate('/app/activity', { replace: true });
      return;
    }

    // 3. Non-home root tabs → navigate to home (map)
    const rootTabs = ['/app/activity', '/app/wallet', '/app/profile', '/app/notifications'];
    if (rootTabs.includes(pathname)) {
      router.navigate('/app/map', { replace: true });
      return;
    }

    // 4. Home tab (map) → exit app
    if (pathname === '/app/map') {
      App.exitApp();
      return;
    }

    // 5. Auth screens → exit app
    if (pathname === '/login' || pathname === '/register') {
      App.exitApp();
      return;
    }

    // Fallback: go to map
    router.navigate('/app/map', { replace: true });
  });

  // Deep link: handle App Link URLs (e.g., payment callbacks)
  App.addListener('appUrlOpen', ({ url }) => {
    try {
      const parsed = new URL(url);
      const path = parsed.pathname;
      const search = parsed.search;
      if (path) {
        router.navigate(path + search);
      }
    } catch (e) {
      console.error('[App] Failed to parse deep link URL:', e);
      import('./lib/errorHandler').then(({ captureError }) => captureError(e, 'App.deepLinkParse', { url }));
    }
  });

}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <DarkModeProvider>
        <VersionGate>
          <RouterProvider router={router} />
          {/* sonner's mobile viewport offset is a flat 16px, so a top-center
              toast lands inside the status bar / Dynamic Island on iOS. Only
              `top` is overridden; unset sides keep sonner's 16px default. */}
          <Toaster
            position="top-center"
            richColors
            closeButton
            mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
          />
        </VersionGate>
      </DarkModeProvider>
    </ErrorBoundary>
  </StrictMode>
);
  